import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface SharedUser {
  id: string;
  display_name: string | null;
  email: string;
  avatar_url: string | null;
  permission_level: 'view' | 'edit';
  shared_at: string;
}

// Hook para contar TODOS os compartilhamentos do usuário (global)
export function useGlobalShareCount() {
  return useQuery({
    queryKey: ['global-share-count'],
    queryFn: async () => {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) return 0;

      const { count: casoSharesCount } = await supabase
        .from('caso_user_shares')
        .select('*', { count: 'exact', head: true })
        .eq('shared_by_user_id', userData.user.id);

      const { count: chatSharesCount } = await supabase
        .from('conversation_user_shares')
        .select('*', { count: 'exact', head: true })
        .eq('shared_by_user_id', userData.user.id);

      return (casoSharesCount || 0) + (chatSharesCount || 0);
    },
  });
}

// Hook para contar compartilhamentos de UMA conversa específica
export function useConversationShareCount(conversationId: string) {
  return useQuery({
    queryKey: ['conversation-share-count', conversationId],
    queryFn: async () => {
      if (!conversationId) return 0;

      // Contar compartilhamentos individuais
      const { count: userSharesCount } = await supabase
        .from('conversation_user_shares')
        .select('*', { count: 'exact', head: true })
        .eq('conversation_id', conversationId);

      // Contar grupos compartilhados e seus membros
      const { data: groupShares } = await supabase
        .from('conversation_group_shares')
        .select(`
          group_id,
          sharing_groups!inner(
            id,
            sharing_group_members(count)
          )
        `)
        .eq('conversation_id', conversationId);

      const groupMembersCount = groupShares?.reduce((total, share: any) => {
        const memberCount = share.sharing_groups?.sharing_group_members?.[0]?.count || 0;
        return total + memberCount;
      }, 0) || 0;

      return (userSharesCount || 0) + groupMembersCount;
    },
    enabled: !!conversationId,
  });
}

// 1. Buscar usuários compartilhados de uma conversa
export function useSharedConversationUsers(conversationId: string) {
  return useQuery<SharedUser[]>({
    queryKey: ['shared-conversation-users', conversationId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('conversation_user_shares')
        .select(`
          permission_level,
          shared_at,
          shared_with_user_id,
          users!conversation_user_shares_shared_with_user_id_fkey(
            id,
            display_name,
            email,
            avatar_url
          )
        `)
        .eq('conversation_id', conversationId);

      if (error) throw error;
      
      return (data || []).map((share: any) => ({
        id: share.users.id,
        display_name: share.users.display_name,
        email: share.users.email,
        avatar_url: share.users.avatar_url,
        permission_level: share.permission_level,
        shared_at: share.shared_at,
      }));
    },
    enabled: !!conversationId,
  });
}

// 2. Compartilhar conversa com usuários
export function useShareConversationMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      conversationId,
      userIds,
      permissionLevel = 'view',
    }: {
      conversationId: string;
      userIds: string[];
      permissionLevel?: 'view' | 'edit';
    }) => {
      // ✅ VERIFICAÇÃO EXPLÍCITA DE SESSÃO
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Sessão expirada. Faça login novamente.');
      }

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('Usuário não autenticado');
      
      const currentUserId = userData.user.id;

      // ✅ Buscar plano do usuário — mais recente ativa
      const { data: subscription } = await supabase
        .from('subscriptions')
        .select('plan_type')
        .eq('user_id', currentUserId)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      const planType = subscription?.plan_type || 'free';

      // ✅ Definir limites de compartilhamento por plano
      const shareLimits: Record<string, number> = {
        free: 0,
        basic: 1,
        pro: 10,
        premium: -1, // ilimitado
      };

      const maxShares = shareLimits[planType] ?? 0;

      // ✅ Buscar TODOS os compartilhamentos do usuário (casos + chats)
      const { count: casoSharesCount } = await supabase
        .from('caso_user_shares')
        .select('*', { count: 'exact', head: true })
        .eq('shared_by_user_id', currentUserId);

      const { count: chatSharesCount } = await supabase
        .from('conversation_user_shares')
        .select('*', { count: 'exact', head: true })
        .eq('shared_by_user_id', currentUserId);

      const totalCurrentShares = (casoSharesCount || 0) + (chatSharesCount || 0);
      const totalAfterShare = totalCurrentShares + userIds.length;

      // ✅ Validar limites
      if (maxShares === 0) {
        throw new Error('Seu plano não permite compartilhamento. Faça upgrade para o plano Básico ou superior.');
      }

      if (maxShares > 0 && totalAfterShare > maxShares) {
        throw new Error(
          `Limite de compartilhamento atingido!\n\n` +
          `📊 Uso atual: ${totalCurrentShares}/${maxShares}\n` +
          `➕ Tentando adicionar: ${userIds.length}\n` +
          `🚫 Total seria: ${totalAfterShare}\n\n` +
          `Seu plano ${planType.toUpperCase()} permite ${maxShares} compartilhamento(s) no total (casos + chats).\n\n` +
          `Atualize para o plano Pro (10) ou Premium (ilimitado).`
        );
      }

      const shares = userIds.map(userId => ({
        conversation_id: conversationId,
        shared_with_user_id: userId,
        shared_by_user_id: currentUserId,
        permission_level: permissionLevel,
      }));

      const { error } = await (supabase as any)
        .from('conversation_user_shares')
        .insert(shares);

      if (error) throw error;

      // Get conversation details
      const { data: convData } = await supabase
        .from('legal_conversations')
        .select('title')
        .eq('id', conversationId)
        .single();

      // Log activity in firm_activity_log
      const activityLogs = userIds.map(userId => ({
        firm_id: '00000000-0000-0000-0000-000000000000',
        event_type: 'chat_shared_user',
        actor_id: currentUserId,
        resource_type: 'chat',
        resource_id: conversationId,
        target_id: userId,
        metadata: {
          conversation_title: convData?.title || 'Conversa sem título',
          permission_level: permissionLevel,
        },
      }));

      await supabase.from('firm_activity_log').insert(activityLogs);

      // Get sender profile
      const { data: senderProfile } = await supabase
        .from('users')
        .select('display_name')
        .eq('id', currentUserId)
        .single();

      const senderName = senderProfile?.display_name || 'Alguém';
      const convTitle = convData?.title || 'uma conversa';

      // Buscar expires_at do primeiro share criado
      const { data: shareData } = await (supabase as any)
        .from('conversation_user_shares')
        .select('expires_at')
        .eq('conversation_id', conversationId)
        .eq('shared_by_user_id', currentUserId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      // Create notifications com client oficial
      const notificationInserts = userIds.map(userId => ({
        type: 'chat_shared',
        title: 'Nova conversa compartilhada',
        message: `${senderName} compartilhou "${convTitle}" com você`,
        target_user_id: userId,
        sent_by: currentUserId, // ✅ Casa com policy sent_by = auth.uid()
        metadata: { 
          conversation_id: conversationId,
          expires_at: shareData?.expires_at || null
        },
      }));

      const { data: notifData, error: notifError } = await supabase
        .from('notifications')
        .insert(notificationInserts)
        .select();

      if (notifError) {
        console.error('❌ Erro ao criar notificações:', notifError);
        toast.error(`Conversa compartilhada, mas notificação falhou: ${notifError.message}`);
      } else {
        console.log('✅ Notificações criadas com sucesso:', notifData);
      }

      return shares;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: ['shared-conversation-users', variables.conversationId] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['conversation-share-count', variables.conversationId] 
      });
      toast.success(`Conversa compartilhada com ${variables.userIds.length} usuário(s)`);
    },
    onError: (error: any) => {
      console.error('Erro completo:', error);
      
      if (error.code === '23505' || error.message?.includes('duplicate')) {
        toast.error('Um ou mais usuários já têm acesso a esta conversa');
      } else {
        toast.error(`Erro ao compartilhar conversa: ${error.message || 'Erro desconhecido'}`);
      }
    },
  });
}

// 3. Remover compartilhamento
export function useUnshareConversationMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      conversationId,
      userId,
    }: {
      conversationId: string;
      userId: string;
    }) => {
      const { error } = await (supabase as any)
        .from('conversation_user_shares')
        .delete()
        .eq('conversation_id', conversationId)
        .eq('shared_with_user_id', userId);

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: ['shared-conversation-users', variables.conversationId] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['conversation-share-count', variables.conversationId] 
      });
      toast.success('Acesso removido com sucesso');
    },
    onError: () => {
      toast.error('Erro ao remover acesso');
    },
  });
}

// 4. Reutilizar useSearchUsers de useShareCaso
export { useSearchUsers } from './useShareCaso';
